import { routeResource, type LockResource } from '@mcpanel/core';
export interface EditSession {
  token: string;
  revision?: string;
  contentHash?: string;
  released?: boolean;
}
const sessions = new Map<string, EditSession>();
const snapshots = new Map<string, { revision?: string; contentHash?: string }>();
export const resourceKey = (resource: LockResource) => `${resource.target}/${resource.screen}`;
export function clearEditSnapshot(resource: LockResource) {
  snapshots.delete(resourceKey(resource));
}
export function setEditSession(resource: LockResource, session?: EditSession) {
  if (session) sessions.set(resourceKey(resource), { ...snapshots.get(resourceKey(resource)), ...session });
  else sessions.delete(resourceKey(resource));
}
export function editHeaders(path: string, body: unknown, method: string) {
  if (method === 'GET' || path.startsWith('/locks')) return {};
  const resource = routeResource(path, body);
  const session = resource && sessions.get(resourceKey(resource));
  if (!session || session.released) return {};
  return {
    'X-Edit-Token': session.token,
    ...(session.revision !== undefined ? { 'If-Match': session.revision } : {}),
    ...(session.contentHash ? { 'X-Edit-Content-Hash': session.contentHash } : {}),
  };
}
export function recordEditRevision(path: string, revision: string | null, contentHash: string | null) {
  const resource = routeResource(path);
  if (resource && !snapshots.has(resourceKey(resource)))
    snapshots.set(resourceKey(resource), { revision: revision ?? undefined, contentHash: contentHash ?? undefined });
  const session = resource && sessions.get(resourceKey(resource));
  if (session && session.contentHash === undefined && contentHash) session.contentHash = contentHash;
  if (session && session.revision === undefined && revision) session.revision = revision;
}
export function editResult(path: string, body: unknown, conflict: boolean) {
  const resource = routeResource(path, body);
  if (resource)
    window.dispatchEvent(new CustomEvent(conflict ? 'edit-conflict' : 'edit-saved', { detail: resourceKey(resource) }));
}

// 書き込みは、その画面のロック取得が終わってから送る。
const guards = new Map<string, () => Promise<void>>();
export function registerEditGuard(resource: LockResource, guard: () => Promise<void>) {
  const key = resourceKey(resource);
  guards.set(key, guard);
  return () => {
    if (guards.get(key) === guard) guards.delete(key);
  };
}
export async function waitForEditLock(method: string, path: string, body?: unknown) {
  if (method === 'GET' || path.startsWith('/locks')) return;
  const resource = routeResource(path, body);
  if (resource) await guards.get(resourceKey(resource))?.();
}
